import { isWeb } from "@stepapp/core";
import type { AddressPayload } from "@stepapp/core/apiTypes";
import { Block, Input, Touchable, TouchableInput, Typography } from "@stepapp/ui";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useState } from "react";
import type { FieldErrors, UseFormSetValue, UseFormTrigger } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ReservationFormParams } from "../../store/draftReservation/types";
import { Grid } from "../Grid";
import AddressAutocompleteModal from "../Modal/AddressAutocompleteModal";

type Props = {
  errors: FieldErrors<{ address: AddressPayload }>;
  setValue: UseFormSetValue<ReservationFormParams>;
  trigger: UseFormTrigger<ReservationFormParams>;
  streetAddress1: string;
  streetAddress2: string;
  doorCode: string;
  postalOrZipCode: string;
  floorArea: number;
  city: string;
  notes?: string;
};

const AddressForm = ({
  errors,
  setValue,
  trigger,
  streetAddress1,
  streetAddress2,
  doorCode,
  postalOrZipCode,
  floorArea,
  city,
  notes,
}: Props) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { slug } = useLocalSearchParams();
  const [isAddressAutocompleteModalVisible, setIsAddressAutocompleteModalVisible] = useState(false);

  const homeSizeValidation = useCallback(() => {
    trigger("address.floorArea");
    trigger("houseArea");
  }, [trigger]);

  const handleSizeChange = useCallback(
    (value: string) => {
      const onlyNumbers = value.replace(/[^0-9]/g, "");
      const newHouseAreaValue = Number.parseFloat(onlyNumbers) || 0;
      setValue("address.floorArea", newHouseAreaValue);
      setValue("houseArea", newHouseAreaValue);

      if (newHouseAreaValue >= 10) {
        homeSizeValidation();
      }
    },
    [setValue, homeSizeValidation],
  );

  const handleOpenAutocomplete = useCallback(() => {
    if (isWeb) {
      return setIsAddressAutocompleteModalVisible(true);
    }

    const path = slug ? `service/${slug}` : "my-home/edit";
    navigate(`/${path}/address-autocomplete`);
  }, [slug, navigate]);

  return (
    <>
      <Grid columns={2} gap={16}>
        <Input
          error={errors?.address?.floorArea?.message}
          testID="flatSize"
          keyboardType="number-pad"
          onBlur={homeSizeValidation}
          onChangeText={handleSizeChange}
          placeholder={t("components.input.placeholder.size")}
          suffixComponent={() => (
            <Typography type="secondary" variant="paragraph">
              {t("screens.flatSize.unit")}
            </Typography>
          )}
          value={`${floorArea}`}
        />
        <TouchableInput
          onPress={handleOpenAutocomplete}
          placeholder={t("components.input.placeholder.postalCode")}
          value={postalOrZipCode}
          error={errors?.address?.postalOrZipCode?.message}
        />
      </Grid>

      <TouchableInput
        testID="address"
        onPress={handleOpenAutocomplete}
        placeholder={t("components.input.placeholder.street")}
        value={streetAddress1}
        error={errors?.address?.streetAddress1?.message}
      />

      <Input
        error={errors?.address?.streetAddress2?.message}
        testID="apartmentNumber"
        onChangeText={(value) =>
          setValue("address.streetAddress2", value, {
            shouldValidate: true,
          })
        }
        placeholder={t("components.input.placeholder.apartmentNumber")}
        value={streetAddress2}
      />
      <Grid columns={2} gap={16}>
        <Touchable onPress={handleOpenAutocomplete}>
          <Input
            asButton
            error={errors?.address?.city?.message}
            placeholder={t("components.input.placeholder.city")}
            value={city}
          />
        </Touchable>

        <Input
          error={errors?.address?.doorCode?.message}
          testID="doorCode"
          onChangeText={(value) => setValue("address.doorCode", value)}
          placeholder={t("components.input.placeholder.doorCode")}
          value={doorCode}
        />
      </Grid>

      <Block marginTop={1}>
        <Block marginBottom={3}>
          <Typography variant="body">
            {t("pages.serviceDateAndAddress.instructionsForArrival")}
          </Typography>
        </Block>
        <Input
          multiline
          error={errors?.address?.notes?.message?.toString()}
          onChangeText={(value) => setValue("address.notes", value)}
          placeholder={t("components.input.placeholder.additionalInstructions")}
          value={notes || ""}
        />
      </Block>

      <AddressAutocompleteModal
        isVisible={isWeb && isAddressAutocompleteModalVisible}
        setModalVisible={setIsAddressAutocompleteModalVisible}
        setValue={setValue}
        streetAddress1={streetAddress1}
      />
    </>
  );
};

export default AddressForm;
